import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import {
  FeesDetailsThunk,
} from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import type { AppDispatch } from "@store/store";
import { useSelector, useDispatch } from "react-redux";
import type { RootReducerType } from "@store/store";
import { postPayloadBkpayThunk } from "@store/thunk/PaymentThunk";
import PaymentLoader from "../../../utils/PaymentLoader";

const FinalLicenseModal = ({
  applicationDetails,
  isFinalPaymentOpen,
  setIsFinalPaymentOpen,
  applicationId,
  applicantName,
  amenityName,
  area,
  locationAddress,
}: {
  applicationDetails: any;
  isFinalPaymentOpen: boolean;
  setIsFinalPaymentOpen: (open: boolean) => void;
  applicationId: number;
  applicantName: string;
  amenityName: string;
  area: string;
  locationAddress: string;
}) => {
  const applicantInfo = {
    name: applicantName,
    amenity: amenityName,
    area: area,
    address: locationAddress,
  };
  const { profile } = useSelector((state: RootReducerType) => state.profile);
  const { address_1, address_2, id, district, state, pincode } =
    applicationDetails;

  const [showLoader, setShowLoader] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (isFinalPaymentOpen && applicationId) {
      dispatch(FeesDetailsThunk({ applicationId }));
    }
  }, [isFinalPaymentOpen, applicationId, dispatch]);

  const { feesDetails, loading, error } = useSelector(
    (state: any) => state.feesDetails,
  );

  const handlePostBase64 = async () => {
    try {
      const fullName = profile?.name || "";
      const nameParts = fullName
        ?.trim()
        .split(" ")
        .filter((part) => part.length > 0);
      const firstName = nameParts?.length > 0 ? nameParts?.[0] : "";
      const lastName =
        nameParts?.length > 1 ? nameParts?.slice(1).join(" ") : "";

      setShowLoader(true);
      let mobileNumber = profile?.mobile_number || "";
      if (mobileNumber.startsWith("+91")) {
        mobileNumber = mobileNumber.slice(3);
      }
      const bkPayload = {
        application_id: id,
        FirstName: firstName || "",
        LastName: lastName || "",
        Address1: profile?.address || "",
        Address2: "",
        PostalCode: profile?.pincode || "",
        City: profile?.district || "",
        StateRegion: profile?.district || "",
        State: profile?.state || "",
        CountryCode: "INDIA",
        MobileNumber: mobileNumber,
        payment_stage: 3,
      };
      const getSignedBase64: any = await dispatch(
        postPayloadBkpayThunk(bkPayload),
      ).unwrap();
      if (getSignedBase64.success === false) {
        toast.error(
          getSignedBase64.message ||
            "Failed to initiate payment. Please try again.",
        );
        setShowLoader(false);
        return;
      }
      const form = document.createElement("form");
      form.method = "POST";
      form.action =
        process.env.REACT_APP_BHARATKOSH_URL || "https://bharatkosh.gov.in/";

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "bharrkkosh";
      input.value = getSignedBase64;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Error posting base64:", error);
      setShowLoader(false);
    }
  };

  const handleFinalPayNow = async () => {
    if (!applicationId) {
      toast.error("Application ID is missing.");
      return;
    }
    await handlePostBase64();
  };

  return (
    <Dialog
      open={isFinalPaymentOpen}
      onClose={() => setIsFinalPaymentOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <PaymentLoader open={showLoader} />
      <Alert severity="error" sx={{ mb: 2, fontSize: "14px" }}>
        <strong>Important:</strong> Please wait for 20 minutes if you have
        recently attempted the payment. If the amount has been deducted but an
        Application Status was not updated, contact our support team at{" "}
        <a
          href="mailto: support-rmp@gov.in"
          style={{ textDecoration: "underline" }}
        >
          {" "}
          support-rmp@gov.in
        </a>{" "}
        for assistance.
      </Alert>
      <DialogTitle className="dialog-title">
        Payment Details
        <IconButton
          aria-label="close"
          onClick={() => setIsFinalPaymentOpen(false)}
          className="close-icon"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers={true} className="dialog-content">
        <Box
          sx={{
            background: "#fafafa",
            borderRadius: "10px",
            p: 2,
            mb: 1,
            fontSize: "16px",
          }}
        >
          <Typography>
            <strong>Applicant Name:</strong> {profile?.name}
          </Typography>
          <Typography>
            <strong>Amenity Name:</strong> {applicantInfo.amenity}
          </Typography>
          <Typography>
            <strong>Area:</strong> {applicantInfo.area}
          </Typography>
          <Typography>
            <strong>Location Address:</strong> {applicantInfo.address}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          className="dialog-description"
          sx={{ mb: 2 }}
        >
          Here is a breakup of the fee to obtain your provisional certificate.
        </Typography>

        <Box
          className="dialog-row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            color: "#888",
            fontSize: "16px",
            mb: 1,
          }}
        >
          <Typography sx={{ color: "#888" }}>Final NOC Fee</Typography>
          <Typography>
            Rs. {feesDetails?.finalLicenseFees?.toLocaleString("en-IN") || "--"}
          </Typography>
        </Box>

        {/* <Box
          className="dialog-row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            color: "#888",
            fontSize: "16px",
            mb: 1
          }}
        >
          <Typography sx={{ color: "#888" }}>Refundable Fee</Typography>
          <Typography sx={{ color: "#888" }}>Rs. 2,00,000.00</Typography>
        </Box> */}

        <Divider sx={{ my: 1, borderStyle: "dashed" }} />

        <Box
          className="dialog-row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 700,
            fontSize: "16px",
          }}
        >
          <Typography>Total</Typography>
          <Typography>
            Rs. {feesDetails?.finalLicenseFees?.toLocaleString("en-IN") || "--"}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "center" }}>
        <Button
          onClick={() => setIsFinalPaymentOpen(false)}
          variant="outlined"
          sx={{
            borderRadius: "24px",
            minWidth: "120px",
            textTransform: "none",
            fontWeight: 500,
            color: "#3A1C73",
            borderColor: "#3A1C73",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={showLoader}
          sx={{
            borderRadius: "24px",
            minWidth: "120px",
            textTransform: "none",
            fontWeight: 500,
            backgroundColor: "#3A1C73",
            "&:hover": { backgroundColor: "#2a145c" },
          }}
          onClick={handleFinalPayNow}
        >
          Pay Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalLicenseModal;
